import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Users, Maximize, Check, X, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { COTTAGES, IMAGES } from "../../data/tilio";
import { Reveal, SectionHeading } from "./Shared";
import { useBooking } from "../../context";

const GALLERY_POOL = [IMAGES.domki1, IMAGES.domki2, IMAGES.domki3, IMAGES.hero];

const CottageDialog = ({ cottage, onClose }) => {
    const openBooking = useBooking();
    const [slide, setSlide] = useState(0);
    const [dir, setDir] = useState(1);

    useEffect(() => {
        setSlide(0);
        setDir(1);
    }, [cottage?.id]);

    const photos = cottage
        ? (cottage.gallery && cottage.gallery.length
            ? cottage.gallery
            : [cottage.image, ...GALLERY_POOL.filter((g) => g !== cottage.image)].map((src, idx) => ({
                  src,
                  alt: `Domek ${cottage.name} — zdjęcie ${idx + 1}`,
              })))
        : [];
    const go = (d) => {
        setDir(d);
        setSlide((s) => (s + d + photos.length) % photos.length);
    };

    const current = photos[slide];

    return (
        <AnimatePresence>
            {cottage && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="fixed inset-0 z-[70] flex items-end justify-center bg-pine/50 backdrop-blur-sm sm:items-center sm:p-6"
                    onClick={onClose}
                    data-testid="cottage-dialog"
                >
                    <motion.div
                        initial={{ y: 60, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 60, opacity: 0 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        data-lenis-prevent
                        className="flex max-h-[94vh] w-full flex-col overflow-y-auto overflow-x-hidden border border-stone bg-ivory sm:w-[min(1120px,94vw)]"
                    >
                        {/* GALLERY */}
                        <div className="relative w-full shrink-0 overflow-hidden bg-sand h-[60vh] sm:h-[56vh] lg:h-[62vh]">
                            <AnimatePresence mode="wait" initial={false}>
                                <motion.div
                                    key={`${cottage.id}-${slide}`}
                                    initial={{ opacity: 0, x: 24 * dir }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -24 * dir }}
                                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                    className="absolute inset-0"
                                >
                                    <img
                                        src={current.src}
                                        alt=""
                                        aria-hidden="true"
                                        className="absolute inset-0 h-full w-full scale-110 object-cover blur-2xl brightness-[0.82] saturate-[0.9]"
                                    />
                                    <div className="absolute inset-0 bg-pine/15" />
                                    <div className="absolute inset-0 flex items-center justify-center p-2 sm:p-4">
                                        <img
                                            src={current.src}
                                            alt={current.alt}
                                            title={current.title || undefined}
                                            className="max-h-full max-w-full object-contain shadow-[0_8px_40px_rgba(15,31,23,0.35)]"
                                            data-testid="gallery-image"
                                        />
                                    </div>
                                </motion.div>
                            </AnimatePresence>
                            <button
                                data-testid="gallery-prev-btn"
                                onClick={() => go(-1)}
                                aria-label="Poprzednie zdjęcie"
                                className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-ivory/85 text-pine backdrop-blur-sm transition-opacity duration-200 hover:opacity-70 sm:left-5"
                            >
                                <ChevronLeft className="h-5 w-5" strokeWidth={1.5} />
                            </button>
                            <button
                                data-testid="gallery-next-btn"
                                onClick={() => go(1)}
                                aria-label="Następne zdjęcie"
                                className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-ivory/85 text-pine backdrop-blur-sm transition-opacity duration-200 hover:opacity-70 sm:right-5"
                            >
                                <ChevronRight className="h-5 w-5" strokeWidth={1.5} />
                            </button>
                            <span
                                data-testid="gallery-counter"
                                className="absolute bottom-4 right-4 rounded-full bg-pine/65 px-3 py-1 text-xs tracking-widest text-ivory backdrop-blur-sm"
                            >
                                {slide + 1} / {photos.length}
                            </span>
                            <button
                                data-testid="cottage-dialog-close"
                                onClick={onClose}
                                className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-ivory/90 text-pine backdrop-blur-sm transition-opacity duration-200 hover:opacity-70"
                                aria-label="Zamknij"
                            >
                                <X className="h-4 w-4" strokeWidth={1.5} />
                            </button>
                        </div>
                        {/* INFO */}
                        <div className="flex flex-1 flex-col gap-6 px-6 py-6 sm:px-8 sm:py-8 lg:flex-row lg:items-center lg:justify-between lg:gap-12 lg:px-10">
                            <div className="lg:flex-1">
                                <p className="overline-label">{cottage.guests} · {cottage.area}</p>
                                <h3 className="mt-2 font-serif text-3xl tracking-[-1px] text-pine sm:text-4xl" data-testid="cottage-dialog-title">
                                    {cottage.name}
                                </h3>
                                <ul className="mt-5 grid grid-cols-1 gap-x-8 sm:grid-cols-2 lg:mt-6 lg:grid-cols-3">
                                    {cottage.features.map((f) => (
                                        <li key={f} className="flex items-center gap-3 py-2.5 text-base text-sage">
                                            <Check className="h-4 w-4 shrink-0 text-pine" strokeWidth={1.5} />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="shrink-0 lg:self-center lg:border-l lg:border-stone/60 lg:pl-12">
                                <button
                                    data-testid="cottage-dialog-availability-btn"
                                    onClick={() => { onClose(); openBooking(cottage.id); }}
                                    className="btn-primary w-full whitespace-nowrap lg:w-auto"
                                >
                                    Sprawdź dostępność
                                    <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default function Cottages() {
    const [selected, setSelected] = useState(null);
    const openBooking = useBooking();

    return (
        <section id="domki" data-testid="cottages-section" className="mx-auto max-w-6xl px-6 py-24 lg:px-10 lg:py-32">
            <SectionHeading
                number="01"
                eyebrow="Trzy domki"
                title="Wybierz domek dla siebie"
                sub="Każdy z prywatnym tarasem, własną sauną lub jacuzzi i rzeką w zasięgu kilku kroków."
                center
                testId="cottages-heading"
            />
            <div className="mt-14 grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-8">
                {COTTAGES.map((c, i) => (
                    <Reveal key={c.id} delay={i * 0.12} className="h-full">
                        <article className="group flex h-full flex-col" data-testid={`cottage-card-${c.id}`}>
                            <button
                                onClick={() => setSelected(c)}
                                className="relative block w-full overflow-hidden"
                                data-testid={`cottage-image-${c.id}`}
                            >
                                <img
                                    src={c.image}
                                    alt={c.imageAlt || `Domek ${c.name}`}
                                    title={c.imageTitle || undefined}
                                    loading="lazy"
                                    className="aspect-[3/4] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                                />
                            </button>
                            <div className="flex flex-1 flex-col pt-7">
                                <div>
                                    <h3 className="font-serif text-[27px] tracking-[-0.3px] text-pine" data-testid={`cottage-name-${c.id}`}>
                                        {c.name}
                                    </h3>
                                    <div className="mt-2 flex items-center gap-4 text-sm text-sage">
                                        <span className="inline-flex items-center gap-1.5">
                                            <Users className="h-4 w-4" strokeWidth={1.25} /> {c.guests}
                                        </span>
                                        <span className="inline-flex items-center gap-1.5">
                                            <Maximize className="h-4 w-4" strokeWidth={1.25} /> {c.area}
                                        </span>
                                    </div>
                                </div>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {c.tags.map((t) => (
                                        <span key={t} className="border border-stone px-2.5 py-1 text-[13px] uppercase tracking-widest text-sage">
                                            {t}
                                        </span>
                                    ))}
                                </div>
                                <p className="mt-4 flex-1 whitespace-pre-line text-[17px] leading-[1.6] text-sage">{c.desc}</p>
                                <div className="mt-7 grid grid-cols-2 gap-3 md:grid-cols-1 xl:grid-cols-2">
                                    <button
                                        data-testid={`cottage-view-btn-${c.id}`}
                                        onClick={() => setSelected(c)}
                                        className="btn-secondary whitespace-nowrap !px-4 !py-2.5 !text-sm"
                                    >
                                        Zobacz domek
                                    </button>
                                    <button
                                        data-testid={`cottage-availability-btn-${c.id}`}
                                        onClick={() => openBooking(c.id)}
                                        className="btn-primary whitespace-nowrap !px-4 !py-2.5 !text-sm"
                                    >
                                        Sprawdź dostępność
                                    </button>
                                </div>
                            </div>
                        </article>
                    </Reveal>
                ))}
            </div>
            <CottageDialog cottage={selected} onClose={() => setSelected(null)} />
        </section>
    );
}
